<template>
  <div class="my-5 mx-5 my-sm-15 mx-sm-10">
    <!-- Header with Title and Import Action -->
    <v-row align="center" class="mb-5">
      <v-col class="text-start">
        <span class="textLight--text font-weight-medium text-h5">
          {{ $t("course.registration") }}
        </span>
      </v-col>
      <v-col class="text-end d-flex justify-end">
        <v-btn
          depressed
          color="primary"
          outlined
          class="rounded-lg font-weight-normal"
          @click="openImportDialog"
        >
          <v-icon left class="me-1">mdi-import</v-icon>
          {{ $t("reports.import") }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Main Registration and Side Layout -->
    <v-row>
      <v-col cols="12">
        <v-row :key="reRender" class="mt-1">
          <v-col cols="12" :md="createOrganization ? 7 : 8" class="mb-6">
            <bulk-registration
              ref="bulkRegistration"
              :selectedOrganization="selectedOrganization"
              :hideAddBtn="createOrganization"
              @reRender="reRenderBulk"
              @add="createOrganization = true"
              @hide="createOrganization = false"
            />
          </v-col>

          <v-col cols="12" :md="createOrganization ? 5 : 4">
            <create-organization
              v-if="createOrganization"
              @onOrganizationCreate="fetchNewOrganizations"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Import Preview & Validation Dialog with Stepper -->
    <v-dialog v-model="importDialog" max-width="950px" persistent>
      <v-card class="rounded-lg">
        <v-card-title class="headline d-flex justify-space-between align-center pb-2">
          <span>Excel Import Wizard</span>
          <v-btn icon @click="closeImportDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-stepper v-model="step" class="elevation-0">
          <v-stepper-header class="elevation-0 border-bottom">
            <v-stepper-step :complete="step > 1" step="1" class="font-weight-medium">
              Select File
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="step > 2" step="2" class="font-weight-medium">
              Preview & Validate
            </v-stepper-step>
          </v-stepper-header>

          <v-stepper-items>
            <!-- Step 1: Select File -->
            <v-stepper-content step="1" class="pa-5">
              <div
                class="border-dashed rounded-lg pa-10 d-flex flex-column align-center justify-center cursor-pointer mb-6"
                :style="dragOver ? 'border: 2px dashed #1976D2; background-color: #E3F2FD;' : 'border: 2px dashed #BDBDBD; background-color: #FAFAFA;'"
                @click="triggerFileSelect"
                @dragover.prevent="dragOver = true"
                @dragleave.prevent="dragOver = false"
                @drop.prevent="onFileDrop"
              >
                <v-icon size="64" color="success" class="mb-4">mdi-microsoft-excel</v-icon>
                <span class="font-weight-medium text-subtitle-1 mb-1">Drag and drop to upload (Excel)</span>
                <span class="textLight--text caption mb-2">or click to browse your files</span>
                <div class="d-flex align-center mt-2">
                  <span class="textLight--text caption">Maximum file size: 2MB</span>
                  <v-icon x-small color="textLight" class="ms-1">mdi-help-circle-outline</v-icon>
                </div>
              </div>

              <!-- Hidden native file input handled via FileSelector -->
              <div style="display: none;">
                <FileSelector
                  ref="fileSelector"
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  @onFileSelect="onFileSelect"
                />
              </div>

              <div class="text-start mb-6">
                <span class="textLight--text subtitle-2">
                  Download a <a href="#" class="primary--text font-weight-medium" @click.prevent="importFrom('xl-sample')">sample Excel file</a> and compare it to your import file to ensure you have the file perfect for the import.
                </span>
              </div>

              <!-- Tips Box -->
              <v-card flat class="grey lighten-4 pa-4 rounded-lg text-start">
                <div class="d-flex align-center mb-2 font-weight-medium text-subtitle-2">
                  <v-icon color="warning" class="me-2" size="20">mdi-lightbulb-on-outline</v-icon>
                  Tips
                </div>
                <ul class="text-subtitle-2 pl-4 textLight--text font-weight-regular" style="list-style-type: disc;">
                  <li class="mb-1">Import data with the details of Employee Name, Employee Email, Employee Id, Department, and Designation.</li>
                  <li>You can download the sample xls file to get detailed information about the data fields used while importing.</li>
                </ul>
              </v-card>
            </v-stepper-content>

            <!-- Step 2: Preview & Validate -->
            <v-stepper-content step="2" class="pa-5">
              <p class="text-start mb-4 textLight--text subtitle-2">
                Please review the details of employees parsed from your Excel sheet. Correct any issues in your file if needed. Only valid rows will be imported.
              </p>
              <v-simple-table fixed-header height="300px" class="border rounded-lg mb-6">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-start">#</th>
                      <th class="text-start">Status</th>
                      <th class="text-start">Name</th>
                      <th class="text-start">Email</th>
                      <th class="text-start">Civil ID/Passport</th>
                      <th class="text-start">Department</th>
                      <th class="text-start">Designation</th>
                      <th class="text-start">Errors / Info</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, idx) in previewRows" :key="idx">
                      <td>{{ idx + 1 }}</td>
                      <td>
                        <v-icon :color="row.isValid ? 'success' : 'error'">
                          {{ row.isValid ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                        </v-icon>
                      </td>
                      <td>{{ row.name }}</td>
                      <td>{{ row.email }}</td>
                      <td>{{ row.civilIdOrPassport }}</td>
                      <td>{{ row.department }}</td>
                      <td>{{ row.designation }}</td>
                      <td>
                        <span v-if="row.errors.length" class="error--text subtitle-2 font-weight-medium">
                          {{ row.errors.join(', ') }}
                        </span>
                        <span v-else class="success--text subtitle-2">Valid</span>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <v-card-actions class="pa-0">
                <v-btn outlined color="primary" @click="step = 1" class="rounded-lg px-4">
                  Back
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn text @click="closeImportDialog" class="rounded-lg">Cancel</v-btn>
                <v-btn
                  color="primary"
                  depressed
                  class="rounded-lg px-4"
                  :disabled="!hasValidRows"
                  @click="confirmImport"
                >
                  Confirm Import ({{ validRowCount }} rows)
                </v-btn>
              </v-card-actions>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import BulkRegistration from "../../course-module/components/BulkRegistration.vue";
import CreateOrganization from "../../organization-module/components/CreateOrganization.vue";
import FileSelector from "@/app/core-module/components/FileSelector";
import {
  excelExport,
  excelImport,
} from "../../reports-module/methods/excelExport";

export default {
  components: {
    BulkRegistration,
    CreateOrganization,
    FileSelector,
  },
  data() {
    return {
      reRender: 0,
      selectedOrganization: null,
      createOrganization: false,
      importDialog: false,
      previewRows: [],
      step: 1,
      dragOver: false,
    };
  },
  computed: {
    validRowCount() {
      return this.previewRows.filter(r => r.isValid).length;
    },
    hasValidRows() {
      return this.validRowCount > 0;
    },
  },
  apollo: {
  },
  methods: {
    reRenderBulk() {
      this.reRender++;
    },
    fetchNewOrganizations(id) {
      if (this.$refs.bulkRegistration) {
        this.$refs.bulkRegistration.refetchOrganizations();
      }
      this.selectedOrganization = id ? id : null;
      this.createOrganization = false;
    },
    importFrom(value) {
      if (value === "xl-sample") {
        const sampleRows = [
          {
            "SL No": 1,
            "Organization*": "CREWPETRO",
            "Department": "IT Department",
            "Employee Name": "John Doe",
            "Employee Id*": "123456789",
            "Employee Email": "john.doe@example.com",
            "Designation": "Software Engineer",
            "Course*": "GAS CONDITIONING AND PROCESSING",
            "Trainer*": "BASIL SALEEM",
            "Course Start Dat": "09/07/2026",
            "CertifiCate Days": 12,
            "Type": "OFFLINE"
          }
        ];
        excelExport(sampleRows, "sample-import-registrations");
      }
    },
    openImportDialog() {
      this.step = 1;
      this.previewRows = [];
      this.importDialog = true;
    },
    closeImportDialog() {
      this.importDialog = false;
    },
    triggerFileSelect() {
      if (this.$refs.fileSelector) {
        this.$refs.fileSelector.selectFile();
      }
    },
    onFileDrop(e) {
      this.dragOver = false;
      const files = e.dataTransfer.files;
      if (files?.length) {
        const file = files[0];
        if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
          const fakeFileObject = {
            name: file.name,
            blob: file
          };
          this.onFileSelect(fakeFileObject);
        } else {
          this.$toast.error("Only Excel files (.xlsx, .xls) are allowed!");
        }
      }
    },
    parseExcelDate(dateVal) {
      if (!dateVal) return null;
      if (typeof dateVal === 'number' || !isNaN(dateVal)) {
        const serial = Number(dateVal);
        const date = new Date((serial - 25569) * 86400 * 1000);
        return this.$moment(date).format('YYYY-MM-DD');
      }
      const parsed = this.$moment(dateVal, ['DD/MM/YYYY', 'YYYY-MM-DD', 'MM/DD/YYYY']);
      if (parsed.isValid()) {
        return parsed.format('YYYY-MM-DD');
      }
      return null;
    },
    matchLocalisedValue(localisedDataList, excelValue) {
      if (!excelValue) return false;
      if (typeof localisedDataList === 'string') {
        return localisedDataList.toLowerCase().trim() === String(excelValue).toLowerCase().trim();
      }
      if (Array.isArray(localisedDataList)) {
        return localisedDataList.some(item => 
          item.value && item.value.toLowerCase().trim() === String(excelValue).toLowerCase().trim()
        );
      }
      if (typeof localisedDataList === 'object' && localisedDataList !== null) {
        return Object.values(localisedDataList).some(val => 
          typeof val === 'string' && val.toLowerCase().trim() === String(excelValue).toLowerCase().trim()
        );
      }
      return false;
    },
    async onFileSelect(file) {
      try {
        const jsonArray = await excelImport(file.blob);
        if (jsonArray?.length) {
          const firstRow = jsonArray[0];
          const bulkReg = this.$refs.bulkRegistration;

          // Helper to fetch keys case-insensitively, trimming spaces and asterisk
          const getExcelVal = (row, possibleKeys) => {
            const normPossible = possibleKeys.map(k => k.toLowerCase().replace(/\*/g, '').replace(/\s+/g, '').trim());
            for (const key of Object.keys(row)) {
              const normKey = key.toLowerCase().replace(/\*/g, '').replace(/\s+/g, '').trim();
              if (normPossible.includes(normKey)) {
                return row[key];
              }
            }
            return null;
          };

          if (bulkReg) {
            // 1. Auto-fill Course
            const excelCourse = getExcelVal(firstRow, ["Course Name", "Course", "Training", "Training Name", "اسم الدورة", "الدورة"]);
            if (excelCourse) {
              const matchedCourse = (bulkReg.courses || []).find(c => 
                this.matchLocalisedValue(c.title, excelCourse)
              );
              if (matchedCourse) {
                bulkReg.course = matchedCourse._id;
                bulkReg.setCourseDuration();
              }
            }

            // 2. Auto-fill Organization
            const excelOrg = getExcelVal(firstRow, ["Organization Name", "Organization", "Company", "Company Name", "الجهة", "الشركة", "المؤسسة"]);
            if (excelOrg) {
              const matchedOrg = (bulkReg.organizations || []).find(o => 
                this.matchLocalisedValue(o.name, excelOrg)
              );
              if (matchedOrg) {
                bulkReg.organization = matchedOrg._id;
                await bulkReg.localFetchEmployees(matchedOrg._id);
              }
            }

            // 3. Auto-fill Trainer
            const excelTrainer = getExcelVal(firstRow, ["Trainer Name", "Trainer", "المدرب"]);
            if (excelTrainer) {
              if (bulkReg.trainerId) {
                await bulkReg.fetchTrainers();
              }
              if (bulkReg.trainersList?.length) {
                const matchedTrainer = bulkReg.trainersList.find(t => 
                  `${t.name || ""} ${t.lastName || ""}`.toLowerCase().trim() === String(excelTrainer).toLowerCase().trim()
                );
                if (matchedTrainer) {
                  bulkReg.trainer = matchedTrainer._id;
                }
              }
            }

            // 4. Auto-fill Start Date
            const excelStartDate = getExcelVal(firstRow, ["Start Date", "StartDate", "Course Start Dat", "Course Start Date", "تاريخ البدء", "تاريخ الدورة", "Date", "تاريخ"]);
            if (excelStartDate) {
              const parsedDate = this.parseExcelDate(excelStartDate);
              if (parsedDate) {
                bulkReg.startDate = parsedDate;
              }
            }

            // 5. Auto-fill Certificate Validity
            const excelValidity = getExcelVal(firstRow, ["Certificate Validity", "Validity", "CertifiCate Days", "Certificate Days", "CertificateDays", "صلاحية الشهادة", "المدة", "صلاحية"]);
            if (excelValidity && !isNaN(excelValidity)) {
              bulkReg.certificateValidity = Number(excelValidity);
            }
          }

          // Generate preview rows
          const rows = [];
          const existingEmails = bulkReg ? bulkReg.users.map(u => u.email.toLowerCase()) : [];
          const existingCivils = bulkReg ? bulkReg.users.map(u => u.civilIdOrPassport.toLowerCase()) : [];

          const seenEmails = new Set();
          const seenCivils = new Set();

          jsonArray.forEach(x => {
            const nameValue = getExcelVal(x, ["Employee Name", "Name", "employeeName", "name", "اسم الموظف", "الاسم"]);
            const emailValue = getExcelVal(x, ["Employee Email", "Email", "email", "Email Address", "البريد الإلكتروني"]);
            const civilIdOrPassportValue = getExcelVal(x, ["Employee Id", "Employee ID", "Civil ID or Passport", "civilIdOrPassport", "Civil ID/Passport No", "Civil ID/Passport", "Civil ID", "Passport", "رقم البطاقة المدنية / جواز السفر", "رقم البطاقة المدنية"]);
            const designationValue = getExcelVal(x, ["Designation", "designation", "المسمى الوظيفي", "الوظيفة"]);
            const departmentValue = getExcelVal(x, ["Department", "department", "القسم", "قسم"]);

            const nameStr = nameValue ? String(nameValue).trim() : "";
            const emailStr = emailValue ? String(emailValue).trim() : "";
            const civilStr = civilIdOrPassportValue ? String(civilIdOrPassportValue).trim() : "";
            const desigStr = designationValue ? String(designationValue).trim() : "";
            const deptStr = departmentValue ? String(departmentValue).trim() : "";

            const errors = [];

            if (!nameStr) errors.push("Missing Name");
            if (!emailStr) errors.push("Missing Email");
            if (!civilStr) errors.push("Missing Civil ID/Passport");
            if (!deptStr) errors.push("Missing Department");

            if (emailStr && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailStr)) {
              errors.push("Invalid Email format");
            }

            if (emailStr && existingEmails.includes(emailStr.toLowerCase())) {
              errors.push("Email already added");
            }
            if (civilStr && existingCivils.includes(civilStr.toLowerCase())) {
              errors.push("Civil ID/Passport already added");
            }

            if (emailStr) {
              if (seenEmails.has(emailStr.toLowerCase())) {
                errors.push("Duplicate Email in Excel");
              } else {
                seenEmails.add(emailStr.toLowerCase());
              }
            }
            if (civilStr) {
              if (seenCivils.has(civilStr.toLowerCase())) {
                errors.push("Duplicate Civil ID/Passport in Excel");
              } else {
                seenCivils.add(civilStr.toLowerCase());
              }
            }

            rows.push({
              name: nameStr,
              email: emailStr,
              civilIdOrPassport: civilStr,
              designation: desigStr,
              department: deptStr,
              errors,
              isValid: errors.length === 0
            });
          });

          this.previewRows = rows;
          this.step = 2; // Automatically switch to Preview step
        } else {
          this.$toast.error("Failed to read file or file is empty!");
        }
      } catch (e) {
        this.$toast.error(e.message || "Some error occurred!");
      }
    },
    confirmImport() {
      const validUsers = this.previewRows
        .filter(r => r.isValid)
        .map(r => {
          let firstName = r.name;
          let lastName = "";
          const spaceIndex = r.name.indexOf(" ");
          if (spaceIndex !== -1) {
            firstName = r.name.substring(0, spaceIndex).trim();
            lastName = r.name.substring(spaceIndex + 1).trim();
          }
          return {
            firstName,
            lastName,
            email: r.email,
            civilIdOrPassport: r.civilIdOrPassport,
            designation: r.designation,
            department: r.department
          };
        });

      if (validUsers.length && this.$refs.bulkRegistration) {
        this.$refs.bulkRegistration.addImportedUsers(validUsers);
        this.$toast.success(`Successfully imported ${validUsers.length} employees.`);
      }
      this.importDialog = false;
      this.step = 1;
    }
  },
};
</script>
